import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { SimplePlayer } from 'src/app/model/simple-player';
import { Validator, ValidatorBase } from '../../../+validation/core/model';
import { AddressValidator } from '../../../+validation/core/model/address-validator';
import { DawaAddressValidatorDirective } from '../../../+validation/directives/dawa-address-validator.directive';
import { RequiredFieldValidatorDirective } from '../../../+validation/directives/required-field-validator.directive';
import { dawaAutocomplete } from '../../../../assets/vendors/dawa/dawa-autocomplete_II.es';
import { Address } from '../../../model/interfaces/address';
import { Country } from '../../../model/interfaces/country';
import { DawaAddress } from '../../../model/interfaces/dawa-address';
import { DawaAddressService } from '../../../services/dawa-address.service';
import { LocationService } from '../../../services/location.service';

declare type parentView = 'my-page' | 'competitions' | 'luckynumber' | 'checkout';

@Component({
  selector: 'app-address',
  styleUrls: ['./address.component.scss', '../../../../assets/scss/modules/_forms.scss'],
  templateUrl: './address.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class AddressComponent extends ValidatorBase implements OnInit, OnDestroy {
  public address: Address;
  public countries: Country[] = [
    { code: 'DK', name: 'Danmark' },
    { code: 'FO', name: 'Færøerne' },
    { code: 'GL', name: 'Grønland' },
  ];
  public labels: { [key: string]: string };
  public selectedCountry: Country;
  public autocomplete = true;
  public dawaInteracted = false;
  public addressForm: UntypedFormGroup;

  public model: {
    selectedDawaAddress: Address;
    selectedAddress: Address;
  };

  @Input('view') public view: parentView;
  @Input('player') public player: SimplePlayer;
  @Input() clearFormEvent: Observable<void>;
  @Input('valid') public parentValid?: boolean;

  @Output('onchange')
      public onchange = new EventEmitter();

  @ViewChild('dawaField', { static: true }) public dawaField: ElementRef;
  @ViewChild('dawaAddressValidator', { static: true }) public dawaAddressValidator: DawaAddressValidatorDirective;
  @ViewChild('streetNameValidator', { static: true }) public streetNameValidator: RequiredFieldValidatorDirective;
  @ViewChild('zipCodeValidator', { static: true }) public zipCodeValidator: RequiredFieldValidatorDirective;
  @ViewChild('cityValidator', { static: true }) public cityValidator: RequiredFieldValidatorDirective;
  @ViewChild('streetNoValidator', { static: true }) public streetNoValidator: RequiredFieldValidatorDirective;

  private validator: Validator;
  private eventsSubscription: Subscription;

  public constructor(
    private dawaAddressService: DawaAddressService,
    private locationService: LocationService,
    private changeDetector: ChangeDetectorRef,
    private viewRef: ViewContainerRef,
    private formBuilder: UntypedFormBuilder
  ) {
     super(viewRef);
  }

  protected isValid(): boolean {
    return this.addressForm.valid;
  }

  public ngOnInit(): void {
    this.detectClearFormEventFromParent();
    this.model = this.setAddressModelByPlayerDetails();

    this.countries = this.locationService.getCountries();

    const selectedCountry =
      this.address != null && this.address.CountryName ? this.address.CountryName : 'Danmark';
    const country = this.countries.find((c) => c.name === selectedCountry);
    this.model.selectedAddress = Object.assign({ CountryName: country.name }, this.address);

    const formAddress = !!this.player ? this.player.Address : null;
    if (formAddress != null) {
      /// replaces string containing ONLY whitespaces with an empty string. Then sets string value to 'Danmark'.
      if (!formAddress.CountryName.replace(/\s/g, '').length) {
        formAddress.CountryName = 'Danmark';
      }
    }

    this.labels = {
      address: this.player != null ? this.dawaAddressService.formatAddress(formAddress) : '',
    };

    if (!this.addressForm) {
      this.buildForm();
    }

    /// Instantiate the dawa component.
    dawaAutocomplete(this.dawaField.nativeElement, {
      select: (selected: DawaAddress) => {
        if (selected.type !== 'adresse') {
          return;
        }

        this.model.selectedDawaAddress = this.dawaAddressService.getAddressByDawaAddress(selected);

        setTimeout(() => {
          this.onAddressBlur(event);
          this.addressChanged();
          setTimeout(() => {
            this.dawaAddressValidator.validate();
          }, 100);
        }, 100);
      },
      minLength: 4,
      adgangsadresserOnly: false,
      fuzzy: true,
    });
  }

  private setAddressModelByPlayerDetails(): {
    selectedDawaAddress: Address;
    selectedAddress: Address;
  } {
    if (this.player) {
      return {
        selectedDawaAddress: this.player ? this.player.Address : null,
        selectedAddress: {
          CityName: this.player.Address.CityName,
          CountryName: this.player.Address.CountryName,
          DawaId: this.player.Address.DawaId,
          StreetName: this.player.Address.StreetName,
          StreetNo: this.player.Address.StreetNo,
          ZipCode: this.player.Address.ZipCode,
          AreaName: this.player.Address.AreaName,
        },
      };
    }

    return {
      selectedDawaAddress: null,
      selectedAddress: {
        CityName: '',
        CountryName: '',
        DawaId: '',
        StreetName: '',
        StreetNo: '',
        ZipCode: '',
        AreaName: '',
      },
    };
  }

  public buildForm() {
    this.addressForm = this.formBuilder.group({
      //chkAddressOnlyValidator: [false],
      DawaAddress: new UntypedFormControl(''),
      StreetName: new UntypedFormControl('', [Validators.required, Validators.pattern(/^[\p{L}\s-]+$/u)]),
      StreetNo: new UntypedFormControl('', [Validators.required, Validators.pattern(/^[0-9]{1,3}[\p{L}\d\s.,-]*$/u)]),
      ZipCode: new UntypedFormControl('', [Validators.required, Validators.pattern(/^[0-9]{4,4}$/)]),
      CityName: new UntypedFormControl('', [Validators.required, Validators.pattern(/^[\p{L}\s-]+$/u)]),
      CountryName: new UntypedFormControl(''),
    });
  }

  public patchDawaAddress(address: Address): void {
    if (address != null) {
      this.addressForm.get('DawaAddress').patchValue(this.dawaAddressService.formatAddress(address));
      this.model.selectedDawaAddress = address;
      this.model.selectedAddress = {
        CityName: address.CityName,
        CountryName: address.CountryName,
        DawaId: address.DawaId,
        StreetName: address.StreetName,
        StreetNo: address.StreetNo,
        ZipCode: address.ZipCode,
        AreaName: address.AreaName,
      };
    }
  }

  get DawaAddress(): AbstractControl {
    return this.addressForm.get('DawaAddress');
  }
  get CountryName(): AbstractControl {
    return this.addressForm.get('countryName');
  }

  public addressChanged(): void {
    this.address = !!this.autocomplete ? this.model.selectedDawaAddress : this.model.selectedAddress;

    if (this.sniff()) {
      this.valid = this.isValid();
      this.onchange.emit();
      this.changeDetector.detectChanges();
    }
  }

  public getAddress(): Address {
    return this.autocomplete ? this.model.selectedDawaAddress : this.model.selectedAddress;
  }

  public getFormattedAddress(): string {
    const address = this.getAddress();
    return address != null ? this.dawaAddressService.formatAddress(address) : '';
  }

  public getValidator(): Validator {
    return this.validator || (this.validator = this.createValidator());
  }

  public checkIfAutocomplete() {
    const dawaCtrl = this.addressForm.get('DawaAddress');

    if (this.autocomplete === false) {
      if(this.dawaAddressValidator.valid === false || this.dawaAddressValidator.address === null) {
        this.streetNameValidator.validate();
        this.streetNoValidator.validate();
        this.zipCodeValidator.validate();
        this.cityValidator.validate();
        }
      dawaCtrl.clearValidators();
    } else {
      dawaCtrl.updateValueAndValidity();

      // Copy the current address to the DawaAddress field
      if (this.model.selectedAddress.StreetName) {
        const a = this.model.selectedAddress;
        const manualAddress = `${a.StreetName} ${a.StreetNo}, ${a.ZipCode} ${a.CityName}`;
        if (manualAddress) {
          if (this.model.selectedDawaAddress == null) {
            this.model.selectedDawaAddress = {} as Address;
            Object.assign(this.model.selectedDawaAddress, {
              StreetName: '',
              StreetNo: '',
              CityName: '',
              ZipCode: ''
            });
          }

          this.model.selectedDawaAddress.StreetName = a.StreetName;
          this.model.selectedDawaAddress.StreetNo = a.StreetNo;
          this.model.selectedDawaAddress.ZipCode = a.ZipCode;
          this.model.selectedDawaAddress.CityName = a.CityName;

          this.addressForm.patchValue({
            DawaAddress: manualAddress
          });
        }
      }
    }
  }

  // Fires when DAWA address field is blurred.
  public onAddressBlur(event): void {
    const address = event?.target?.value === '' ? null: this.model.selectedDawaAddress;
    if (address == null) {
      
      this.labels.address = '';
      Object.assign(this.model.selectedDawaAddress, {
        StreetName: '',
        StreetNo: '',
        CityName: '',
        ZipCode: ''
      });
      Object.assign(this.model.selectedAddress, {
        StreetName: '',
        StreetNo: '',
        CityName: '',
        ZipCode: ''
      });
    } else {
      this.patchDawaAddress(address);
      const formattedAddress = this.dawaAddressService.formatAddress(address);
      this.labels.address = formattedAddress;
      if (event != null) {
        event.target.value = formattedAddress;
      }
      this.getValidator().sniff();
      this.changeDetector.detectChanges();
    }
  }

  public onFieldBlur(event: Event): void {
    this.changeDetector.detectChanges();
    this.validator.sniff();
  }

  public onDawaInput(): void {
    if (!this.dawaInteracted && this.dawaField.nativeElement.value.length >= 3) {
      this.dawaAddressValidator.sniff();
      this.dawaAddressValidator.valid = true;
      this.dawaInteracted = true;
    }
  }

  private createValidator(): Validator {
    const validationStrategies = {
      //dawa: [this.dawaAddressValidator],
      validate: [this.dawaAddressValidator, this.streetNameValidator, this.streetNoValidator, this.zipCodeValidator, this.cityValidator],
    };

    const resolveValidationStrategy: () => Validator[] = () => {
      return this.autocomplete ? validationStrategies.validate : null;
    };

    this.validator = new AddressValidator(resolveValidationStrategy, this.element);

    return this.validator;
  }

  public detectClearFormEventFromParent(): void {
    if (this.clearFormEvent) {
      this.eventsSubscription = this.clearFormEvent.subscribe(() => this.clearForm());
    }
  }

  public clearForm(): void {
    this.addressForm.reset();
  }

  public ngOnDestroy(): void {
    if (this.eventsSubscription) {
      this.eventsSubscription.unsubscribe();
    }
  }
}
